class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.activeElement = null;
    this.recommendationsRequest = null;
    this.recommendationsKey = '';
    this.syncTimer = null;
    this.toastTimer = null;
    this.pendingUIState = null;

    /*
     * Fast quantity controls
     *
     * Dawn normally waits for a debounced change event.
     * We intercept + / − clicks, update the number immediately,
     * combine rapid taps for 60ms, then call Dawn's own
     * updateQuantity method.
     */
    this.quantityUpdateTimers = new Map();

    this.onFastQuantityClick =
      this.onFastQuantityClick.bind(this);

    this.onDrawerClick =
      this.onDrawerClick.bind(this);

    this.onDrawerSubmit =
      this.onDrawerSubmit.bind(this);

    this.onDrawerChange =
      this.onDrawerChange.bind(this);

    this.onDrawerKeyup =
      this.onDrawerKeyup.bind(this);

    this.onDocumentClick =
      this.onDocumentClick.bind(this);

    this.onDocumentKeydown =
      this.onDocumentKeydown.bind(this);

    this.onDocumentPointerDown =
      this.onDocumentPointerDown.bind(this);

    /*
     * Capture phase is important here.
     * It lets us intercept quantity button clicks before
     * Dawn's native quantity-input element handles them.
     */
    this.addEventListener(
      'click',
      this.onFastQuantityClick,
      true
    );

    this.addEventListener(
      'click',
      this.onDrawerClick
    );

    this.addEventListener(
      'submit',
      this.onDrawerSubmit
    );

    this.addEventListener(
      'change',
      this.onDrawerChange
    );

    this.addEventListener(
      'keyup',
      this.onDrawerKeyup
    );

    document.addEventListener(
      'click',
      this.onDocumentClick
    );

    document.addEventListener(
      'keydown',
      this.onDocumentKeydown
    );

    document.addEventListener(
      'pointerdown',
      this.onDocumentPointerDown
    );

    this.observer = new MutationObserver(
      (mutations) => {
        this.scheduleSync();

        const drawerContentWasReplaced =
          mutations.some((mutation) => {
            const target = mutation.target;

            return (
              target instanceof Element &&
              (
                target.classList.contains(
                  'drawer__inner'
                ) ||
                target.id === 'CartDrawer'
              )
            );
          });

        if (
          drawerContentWasReplaced &&
          this.pendingUIState
        ) {
          const snapshot =
            this.pendingUIState;

          this.pendingUIState = null;

          requestAnimationFrame(() => {
            this.restoreUIState(snapshot);
          });
        }
      }
    );

    this.observer.observe(this, {
      childList: true,
      subtree: true,
    });

    this.setHeaderCartIconAccessibility();
    this.scheduleSync();
  }

  disconnectedCallback() {
    this.removeEventListener(
      'click',
      this.onFastQuantityClick,
      true
    );

    this.removeEventListener(
      'click',
      this.onDrawerClick
    );

    this.removeEventListener(
      'submit',
      this.onDrawerSubmit
    );

    this.removeEventListener(
      'change',
      this.onDrawerChange
    );

    this.removeEventListener(
      'keyup',
      this.onDrawerKeyup
    );

    document.removeEventListener(
      'click',
      this.onDocumentClick
    );

    document.removeEventListener(
      'keydown',
      this.onDocumentKeydown
    );

    document.removeEventListener(
      'pointerdown',
      this.onDocumentPointerDown
    );

    this.observer?.disconnect();
    this.recommendationsRequest?.abort();

    clearTimeout(this.syncTimer);
    clearTimeout(this.toastTimer);

    this.quantityUpdateTimers.forEach(
      (timer) => {
        clearTimeout(timer);
      }
    );

    this.quantityUpdateTimers.clear();
  }

  /* ======================================================
     FAST QUANTITY BUTTONS
     ====================================================== */

  onFastQuantityClick(event) {
    if (!(event.target instanceof Element)) {
      return;
    }

    const button = event.target.closest(
      '.gt-cart-quantity .quantity__button'
    );

    if (!button || !this.contains(button)) {
      return;
    }

    if (
      button.disabled ||
      button.getAttribute('aria-disabled') ===
        'true'
    ) {
      return;
    }

    const quantityElement = button.closest(
      'quantity-input'
    );

    const input = quantityElement?.querySelector(
      '.quantity__input'
    );

    const cartDrawerItems = button.closest(
      'cart-drawer-items'
    );

    if (!input || !cartDrawerItems) {
      return;
    }

    /*
     * Prevent Dawn's quantity-input element from also
     * processing this click and triggering a second update.
     */
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const currentQuantity =
      Number.parseInt(input.value, 10) || 0;

    const step =
      Math.max(
        1,
        Number.parseInt(input.step, 10) || 1
      );

    const minimumPurchaseQuantity =
      Math.max(
        1,
        Number.parseInt(
          input.dataset.min,
          10
        ) || 1
      );

    const maximumQuantity =
      input.max !== ''
        ? Number.parseInt(input.max, 10)
        : Number.POSITIVE_INFINITY;

    let nextQuantity = currentQuantity;

    if (button.name === 'plus') {
      nextQuantity =
        currentQuantity + step;

      if (
        Number.isFinite(maximumQuantity)
      ) {
        nextQuantity = Math.min(
          nextQuantity,
          maximumQuantity
        );
      }
    }

    if (button.name === 'minus') {
      const proposedQuantity =
        currentQuantity - step;

      /*
       * Standard products can go from 1 to 0,
       * which removes the item.
       *
       * Products with a minimum quantity greater
       * than 1 stay at that minimum instead of
       * entering an invalid quantity.
       */
      if (
        minimumPurchaseQuantity > 1 &&
        proposedQuantity <
          minimumPurchaseQuantity
      ) {
        nextQuantity = currentQuantity;
      } else {
        nextQuantity = Math.max(
          0,
          proposedQuantity
        );
      }
    }

    if (
      nextQuantity === currentQuantity
    ) {
      return;
    }

    /*
     * Optimistic update:
     * the customer sees the new quantity immediately,
     * before the Shopify request has completed.
     */
    input.value = String(nextQuantity);
    input.setAttribute(
      'aria-busy',
      'true'
    );

    const cartItem = button.closest(
      '.gt-cart-item'
    );

    cartItem?.classList.add(
      'gt-cart-item--quantity-pending'
    );

    /*
     * Allow any quantity-input UI logic that listens
     * for input events to update, without dispatching
     * Dawn's slower change event.
     */
    input.dispatchEvent(
      new Event('input', {
        bubbles: true,
      })
    );

    this.pendingUIState =
      this.captureUIState();

    this.scheduleFastQuantityUpdate({
      input,
      button,
      cartDrawerItems,
      quantity: nextQuantity,
    });
  }

  scheduleFastQuantityUpdate({
    input,
    button,
    cartDrawerItems,
    quantity,
  }) {
    const line =
      input.dataset.index ||
      input.id ||
      input.dataset.quantityVariantId;

    if (!line) {
      this.fallbackQuantityChange(input);
      return;
    }

    const existingTimer =
      this.quantityUpdateTimers.get(line);

    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    /*
     * Sixty milliseconds is enough to combine very fast
     * repeated taps without introducing a noticeable wait.
     */
    const timer = window.setTimeout(() => {
      this.quantityUpdateTimers.delete(line);

      this.runFastQuantityUpdate({
        input,
        button,
        cartDrawerItems,
        line,
        quantity,
      });
    }, 60);

    this.quantityUpdateTimers.set(
      line,
      timer
    );
  }

  runFastQuantityUpdate({
    input,
    button,
    cartDrawerItems,
    line,
    quantity,
  }) {
    /*
     * The user may have tapped several times during
     * the 60ms window. Always use the latest number
     * shown in the input.
     */
    const latestQuantity =
      Number.parseInt(input.value, 10);

    const finalQuantity =
      Number.isFinite(latestQuantity)
        ? latestQuantity
        : quantity;

    const lineIndex =
      Number.parseInt(
        input.dataset.index || line,
        10
      );

    const variantId =
      input.dataset.quantityVariantId;

    if (
      !Number.isFinite(lineIndex) ||
      typeof cartDrawerItems.updateQuantity !==
        'function'
    ) {
      this.fallbackQuantityChange(input);
      return;
    }

    /*
     * Dawn has used two updateQuantity signatures:
     *
     * Older versions:
     * updateQuantity(line, quantity, name, variantId)
     *
     * Newer versions:
     * updateQuantity(
     *   line,
     *   quantity,
     *   event,
     *   name,
     *   variantId
     * )
     *
     * Checking the function length keeps this compatible
     * with both.
     */
    if (
      cartDrawerItems.updateQuantity.length >=
      5
    ) {
      const quantityEvent = {
        currentTarget: button,
        target: input,
      };

      cartDrawerItems.updateQuantity(
        lineIndex,
        finalQuantity,
        quantityEvent,
        button.name,
        variantId
      );

      return;
    }

    cartDrawerItems.updateQuantity(
      lineIndex,
      finalQuantity,
      button.name,
      variantId
    );
  }

  fallbackQuantityChange(input) {
    /*
     * Safety fallback for an unusual Dawn version.
     * The native cart behaviour will still work, though
     * it may use Dawn's original debounce.
     */
    input.removeAttribute('aria-busy');

    input.dispatchEvent(
      new Event('change', {
        bubbles: true,
      })
    );
  }

  /* ======================================================
     DRAWER EVENTS
     ====================================================== */

  onDocumentPointerDown(event) {
    if (
      !this.classList.contains('active')
    ) {
      return;
    }

    if (!(event.target instanceof Element)) {
      return;
    }

    const inner = this.querySelector(
      '.drawer__inner'
    );

    if (!inner) return;

    if (inner.contains(event.target)) {
      return;
    }

    if (
      event.target.closest(
        '#cart-icon-bubble'
      )
    ) {
      return;
    }

    this.close();
  }

  onDocumentClick(event) {
    if (!(event.target instanceof Element)) {
      return;
    }

    const cartLink = event.target.closest(
      '#cart-icon-bubble'
    );

    if (!cartLink) return;

    event.preventDefault();
    this.open(cartLink);
  }

  onDocumentKeydown(event) {
    if (!(event.target instanceof Element)) {
      return;
    }

    const cartLink = event.target.closest(
      '#cart-icon-bubble'
    );

    if (!cartLink) return;

    if (
      event.code.toUpperCase() !== 'SPACE' &&
      event.code.toUpperCase() !== 'ENTER'
    ) {
      return;
    }

    event.preventDefault();
    this.open(cartLink);
  }

  onDrawerKeyup(event) {
    if (event.code === 'Escape') {
      this.close();
    }
  }

  onDrawerClick(event) {
    if (!(event.target instanceof Element)) {
      return;
    }

    if (
      event.target.closest(
        '[data-cart-drawer-close]'
      )
    ) {
      event.preventDefault();
      this.close();
      return;
    }

    const discountRemoveButton =
      event.target.closest(
        '[data-remove-discount-code]'
      );

    if (discountRemoveButton) {
      event.preventDefault();

      this.removeDiscountCode(
        discountRemoveButton
      );

      return;
    }

    const checkoutButton =
      event.target.closest(
        '[data-cart-checkout]'
      );

    if (checkoutButton) {
      event.preventDefault();

      this.startCheckout(
        checkoutButton
      );

      return;
    }

    const recommendationButton =
      event.target.closest(
        '[data-recommendation-add]'
      );

    if (recommendationButton) {
      event.preventDefault();

      this.addRecommendedProduct(
        recommendationButton
      );

      return;
    }

    const scrollButton =
      event.target.closest(
        '[data-recommendation-scroll]'
      );

    if (scrollButton) {
      event.preventDefault();

      this.scrollRecommendations(
        Number(
          scrollButton.dataset
            .recommendationScroll || 0
        )
      );
    }
  }

  onDrawerSubmit(event) {
    if (!(event.target instanceof Element)) {
      return;
    }

    const discountForm =
      event.target.closest(
        '#CartDrawer-DiscountForm'
      );

    if (!discountForm) return;

    event.preventDefault();
    this.applyDiscount(discountForm);
  }

  onDrawerChange(event) {
    if (!(event.target instanceof Element)) {
      return;
    }

    const quantityInput =
      event.target.closest(
        '.gt-cart-quantity .quantity__input'
      );

    if (quantityInput) {
      this.pendingUIState =
        this.captureUIState();

      return;
    }

    const variantSelect =
      event.target.closest(
        '[data-recommendation-variant]'
      );

    if (!variantSelect) return;

    const selectedOption =
      variantSelect.options[
        variantSelect.selectedIndex
      ];

    const card = variantSelect.closest(
      '[data-recommendation-card]'
    );

    const button = card?.querySelector(
      '[data-recommendation-add]'
    );

    const price = card?.querySelector(
      '[data-recommendation-price]'
    );

    const image = card?.querySelector(
      '[data-recommendation-image]'
    );

    if (button) {
      button.dataset.variantId =
        selectedOption.value;
    }

    if (price) {
      price.textContent =
        this.formatMoney(
          Number(
            selectedOption.dataset.price
          ),
          variantSelect.dataset.currency
        );
    }

    if (
      image &&
      selectedOption.dataset.image
    ) {
      image.src =
        selectedOption.dataset.image;
    }

    this.updateRecommendationShippingBadge(
      card,
      Number(selectedOption.dataset.price)
    );
  }

  setHeaderCartIconAccessibility() {
    const cartLink =
      document.querySelector(
        '#cart-icon-bubble'
      );

    if (!cartLink) return;

    cartLink.setAttribute(
      'role',
      'button'
    );

    cartLink.setAttribute(
      'aria-haspopup',
      'dialog'
    );

    cartLink.setAttribute(
      'aria-controls',
      'CartDrawer'
    );
  }

  open(triggeredBy) {
    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }

    this.classList.add('animate');

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.add('active');
      });
    });

    document.body.classList.add(
      'overflow-hidden'
    );

    this.loadRecommendations();

    window.setTimeout(() => {
      if (
        !this.classList.contains('active')
      ) {
        return;
      }

      const focusContainer =
        this.classList.contains('is-empty')
          ? this.querySelector(
              '.drawer__inner-empty'
            )
          : this.querySelector(
              '.drawer__inner'
            );

      const focusElement =
        this.querySelector(
          '.drawer__close'
        ) ||
        this.querySelector(
          '.drawer__inner'
        );

      if (
        focusContainer &&
        focusElement &&
        typeof trapFocus === 'function'
      ) {
        trapFocus(
          focusContainer,
          focusElement
        );
      }
    }, 280);
  }

  close() {
    this.classList.remove('active');

    if (
      typeof removeTrapFocus === 'function'
    ) {
      removeTrapFocus(
        this.activeElement
      );
    }

    document.body.classList.remove(
      'overflow-hidden'
    );

    window.setTimeout(() => {
      if (
        !this.classList.contains('active')
      ) {
        this.classList.remove('animate');
      }
    }, 280);
  }

  startCheckout(button) {
    if (
      button.classList.contains(
        'is-loading'
      )
    ) {
      return;
    }

    button.classList.add('is-loading');

    button.setAttribute(
      'aria-busy',
      'true'
    );

    const label = button.querySelector(
      '[data-checkout-label]'
    );

    if (label) {
      label.textContent =
        'Opening secure checkout…';
    }

    const root =
      window.Shopify?.routes?.root || '/';

    window.setTimeout(() => {
      window.location.assign(
        `${root}checkout`
      );
    }, 80);
  }

  setLoading(
    isLoading,
    message = 'Updating your cart…'
  ) {
    this.classList.toggle(
      'is-loading',
      isLoading
    );

    this.setAttribute(
      'aria-busy',
      isLoading ? 'true' : 'false'
    );

    const loadingElement =
      this.querySelector(
        '.gt-cart-drawer__loading'
      );

    if (!loadingElement) return;

    loadingElement.setAttribute(
      'aria-hidden',
      isLoading ? 'false' : 'true'
    );

    const text =
      loadingElement.querySelector(
        'span:last-child'
      );

    if (text) {
      text.textContent = message;
    }
  }

  captureUIState() {
    const scrollContainer =
      this.querySelector(
        '.gt-cart-drawer__scroll'
      );

    const recommendationViewport =
      this.querySelector(
        '[data-recommendations-viewport]'
      );

    const discountDetails =
      this.querySelector(
        '.gt-cart-discount-details'
      );

    return {
      wasOpen:
        this.classList.contains('active'),

      scrollTop:
        scrollContainer?.scrollTop || 0,

      recommendationIndex:
        recommendationViewport
          ? this.getRecommendationIndex(
              recommendationViewport
            )
          : 0,

      discountOpen:
        Boolean(discountDetails?.open),
    };
  }

  restoreUIState(snapshot) {
    if (!snapshot) return;

    const scrollContainer =
      this.querySelector(
        '.gt-cart-drawer__scroll'
      );

    const recommendationViewport =
      this.querySelector(
        '[data-recommendations-viewport]'
      );

    const discountDetails =
      this.querySelector(
        '.gt-cart-discount-details'
      );

    if (scrollContainer) {
      scrollContainer.scrollTop =
        snapshot.scrollTop;
    }

    if (recommendationViewport) {
      recommendationViewport.scrollLeft =
        recommendationViewport.clientWidth *
        snapshot.recommendationIndex;
    }

    if (
      discountDetails &&
      snapshot.discountOpen
    ) {
      discountDetails.open = true;
    }

    this.updateRecommendationCounter();
  }

  renderContents(
    parsedState,
    {
      toastMessage = '',
      preserveScroll = true,
    } = {}
  ) {
    if (!parsedState?.sections) return;

    const snapshot = preserveScroll
      ? this.captureUIState()
      : {
          wasOpen:
            this.classList.contains(
              'active'
            ),

          scrollTop: 0,
          recommendationIndex: 0,
          discountOpen: false,
        };

    this.recommendationsRequest?.abort();

    this.getSectionsToRender().forEach(
      (section) => {
        const html =
          parsedState.sections[
            section.id
          ];

        if (!html) return;

        const sectionElement =
          section.selector
            ? document.querySelector(
                section.selector
              )
            : document.getElementById(
                section.id
              );

        if (!sectionElement) return;

        const replacement =
          this.getSectionInnerHTML(
            html,
            section.selector
          );

        if (replacement === null) {
          return;
        }

        sectionElement.innerHTML =
          replacement;
      }
    );

    this.recommendationsKey = '';

    this.setHeaderCartIconAccessibility();
    this.syncState();
    this.setLoading(false);

    requestAnimationFrame(() => {
      this.restoreUIState(snapshot);

      if (snapshot.wasOpen) {
        this.classList.add(
          'animate',
          'active'
        );

        document.body.classList.add(
          'overflow-hidden'
        );
      } else {
        this.open();
      }

      if (toastMessage) {
        window.setTimeout(() => {
          this.showToast(
            toastMessage
          );
        }, 80);
      }
    });
  }

  getSectionInnerHTML(
    html,
    selector = '.shopify-section'
  ) {
    const parsedDocument =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );

    const element =
      parsedDocument.querySelector(
        selector
      );

    return element
      ? element.innerHTML
      : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(
    html,
    selector = '.shopify-section'
  ) {
    return new DOMParser()
      .parseFromString(
        html,
        'text/html'
      )
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  scheduleSync() {
    window.clearTimeout(
      this.syncTimer
    );

    this.syncTimer =
      window.setTimeout(() => {
        this.syncState();
      }, 40);
  }

  syncState() {
    const isEmpty =
      !this.querySelector(
        '.gt-cart-item'
      );

    this.classList.toggle(
      'is-empty',
      isEmpty
    );

    this.setHeaderCartIconAccessibility();
    this.bindRecommendationViewport();

    if (!isEmpty) {
      this.loadRecommendations();
    }
  }

  bindRecommendationViewport() {
    const viewport =
      this.querySelector(
        '[data-recommendations-viewport]'
      );

    if (
      !viewport ||
      viewport.dataset.counterBound ===
        'true'
    ) {
      return;
    }

    viewport.dataset.counterBound =
      'true';

    let ticking = false;

    viewport.addEventListener(
      'scroll',
      () => {
        if (ticking) return;

        ticking = true;

        requestAnimationFrame(() => {
          this.updateRecommendationCounter();
          ticking = false;
        });
      }
    );

    this.updateRecommendationCounter();
  }

  getRecommendationIndex(viewport) {
    if (!viewport?.clientWidth) {
      return 0;
    }

    return Math.max(
      0,
      Math.round(
        viewport.scrollLeft /
          viewport.clientWidth
      )
    );
  }

  updateRecommendationCounter() {
    const container =
      this.querySelector(
        '#CartDrawer-Recommendations'
      );

    const viewport =
      container?.querySelector(
        '[data-recommendations-viewport]'
      );

    const counter =
      container?.querySelector(
        '[data-recommendation-counter]'
      );

    const previousButton =
      container?.querySelector(
        '[data-recommendation-scroll="-1"]'
      );

    const nextButton =
      container?.querySelector(
        '[data-recommendation-scroll="1"]'
      );

    const cards = container
      ? Array.from(
          container.querySelectorAll(
            '[data-recommendation-card]'
          )
        )
      : [];

    if (
      !viewport ||
      !counter ||
      !cards.length
    ) {
      return;
    }

    const index = Math.min(
      this.getRecommendationIndex(
        viewport
      ),
      cards.length - 1
    );

    counter.textContent =
      `${index + 1} / ${cards.length}`;

    if (previousButton) {
      previousButton.disabled =
        index <= 0;
    }

    if (nextButton) {
      nextButton.disabled =
        index >= cards.length - 1;
    }
  }

  async loadRecommendations() {
    const container =
      this.querySelector(
        '#CartDrawer-Recommendations'
      );

    if (!container) return;

    const cartProductIds = (
      container.dataset.cartProductIds ||
      ''
    )
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);

    const fallbackProductId =
      container.dataset.productId;

    const seedProductIds =
      cartProductIds.length
        ? cartProductIds
        : fallbackProductId
          ? [fallbackProductId]
          : [];

    if (!seedProductIds.length) {
      container.hidden = true;
      return;
    }

    const requestKey =
      seedProductIds.join('|');

    if (
      this.recommendationsKey ===
        requestKey &&
      container.dataset
        .recommendationsLoaded === 'true'
    ) {
      return;
    }

    this.recommendationsKey =
      requestKey;

    container.dataset
      .recommendationsLoaded = 'false';

    container.setAttribute(
      'aria-busy',
      'true'
    );

    this.recommendationsRequest?.abort();

    this.recommendationsRequest =
      new AbortController();

    const requestSignal =
      this.recommendationsRequest.signal;

    try {
      const initialProducts =
        await this.fetchRecommendationPair(
          seedProductIds[0],
          cartProductIds
        );

      if (
        requestSignal.aborted ||
        !this.isConnected
      ) {
        return;
      }

      const recommendationMap =
        new Map();

      initialProducts.forEach(
        (product) => {
          recommendationMap.set(
            String(product.id),
            product
          );
        }
      );

      if (!recommendationMap.size) {
        container.hidden = true;

        container.setAttribute(
          'aria-busy',
          'false'
        );

        return;
      }

      this.renderRecommendedProducts(
        container,
        Array.from(
          recommendationMap.values()
        ).slice(0, 20)
      );

      container.dataset
        .recommendationsLoaded = 'true';

      const completedProducts =
        await this
          .collectAdditionalRecommendations(
            seedProductIds,
            cartProductIds,
            recommendationMap,
            20
          );

      if (
        requestSignal.aborted ||
        !this.isConnected
      ) {
        return;
      }

      const currentContainer =
        this.querySelector(
          '#CartDrawer-Recommendations'
        );

      if (!currentContainer) return;

      this.renderRecommendedProducts(
        currentContainer,
        completedProducts
      );
    } catch (error) {
      if (
        error.name !== 'AbortError'
      ) {
        console.error(
          'Cart recommendations failed:',
          error
        );
      }

      const currentContainer =
        this.querySelector(
          '#CartDrawer-Recommendations'
        );

      if (currentContainer) {
        currentContainer.setAttribute(
          'aria-busy',
          'false'
        );

        if (
          !currentContainer.querySelector(
            '[data-recommendation-card]'
          )
        ) {
          currentContainer.hidden = true;
        }
      }
    }
  }

  async collectAdditionalRecommendations(
    initialSeedProductIds,
    cartProductIds,
    recommendationMap,
    targetCount
  ) {
    const queue = [
      ...initialSeedProductIds
        .slice(1)
        .map(String),

      ...Array.from(
        recommendationMap.keys()
      ),
    ];

    const queuedIds = new Set(queue);

    const processedIds = new Set([
      String(initialSeedProductIds[0]),
    ]);

    const maximumAdditionalSeeds = 8;
    let processedSeedCount = 0;

    while (
      queue.length &&
      recommendationMap.size <
        targetCount &&
      processedSeedCount <
        maximumAdditionalSeeds
    ) {
      const seedProductId =
        String(queue.shift());

      if (
        !seedProductId ||
        processedIds.has(
          seedProductId
        )
      ) {
        continue;
      }

      processedIds.add(seedProductId);
      processedSeedCount += 1;

      const products =
        await this.fetchRecommendationPair(
          seedProductId,
          cartProductIds
        );

      products.forEach((product) => {
        const productId =
          String(product.id);

        if (
          !recommendationMap.has(
            productId
          )
        ) {
          recommendationMap.set(
            productId,
            product
          );
        }

        if (
          !processedIds.has(productId) &&
          !queuedIds.has(productId)
        ) {
          queue.push(productId);
          queuedIds.add(productId);
        }
      });
    }

    return Array.from(
      recommendationMap.values()
    ).slice(0, targetCount);
  }

  async fetchRecommendationPair(
    productId,
    cartProductIds
  ) {
    const safelyFetch =
      async (intent) => {
        try {
          return await this
            .fetchRecommendedProducts(
              productId,
              cartProductIds,
              intent
            );
        } catch (error) {
          if (
            error.name === 'AbortError'
          ) {
            throw error;
          }

          return [];
        }
      };

    const [
      related,
      complementary,
    ] = await Promise.all([
      safelyFetch('related'),
      safelyFetch('complementary'),
    ]);

    const combined = new Map();

    [
      ...related,
      ...complementary,
    ].forEach((product) => {
      combined.set(
        String(product.id),
        product
      );
    });

    return Array.from(
      combined.values()
    );
  }

  async fetchRecommendedProducts(
    productId,
    cartProductIds,
    intent
  ) {
    const root =
      window.Shopify?.routes?.root || '/';

    const endpoint =
      `${root}recommendations/products.json` +
      `?product_id=${encodeURIComponent(
        productId
      )}` +
      '&limit=10' +
      `&intent=${intent}`;

    const response = await fetch(
      endpoint,
      {
        signal:
          this.recommendationsRequest
            .signal,

        headers: {
          Accept: 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        `Recommendations request failed with ${response.status}`
      );
    }

    const data =
      await response.json();

    const productsInCart =
      new Set(
        cartProductIds.map(String)
      );

    return (data.products || [])
      .filter((product) => {
        const hasAvailableVariant = (
          product.variants || []
        ).some(
          (variant) =>
            variant.available
        );

        // Product type is the authoritative category. Some older teas have no type.
        const productType = String(product.type || product.product_type || '').trim();
        const title = String(product.title || '');
        const isTea = productType
          ? /(?:^|\s)tea$/i.test(productType) || /^matcha powder$/i.test(productType)
          : /\btea\b/i.test(title) && !/\b(?:gift\s*card|voucher|infuser|strainer|teapot|teacup|scoop|kettle|canister|bags?|bundle|kit|sample box)\b/i.test(title);
        return (
          isTea &&
          product.available &&
          hasAvailableVariant &&
          !productsInCart.has(
            String(product.id)
          )
        );
      });
  }

  renderRecommendedProducts(
    container,
    products
  ) {
    const viewport =
      container.querySelector(
        '[data-recommendations-viewport]'
      );

    const previousIndex = viewport
      ? this.getRecommendationIndex(
          viewport
        )
      : 0;

    const currency =
      container.dataset.currency ||
      window.Shopify?.currency?.active ||
      'AUD';

    const freeShippingRemaining =
      Number(
        container.dataset
          .freeShippingRemaining || 0
      );

    const cards = products
      .map((product) =>
        this.buildRecommendationCard(
          product,
          currency,
          freeShippingRemaining
        )
      )
      .filter(Boolean)
      .join('');

    if (!cards) {
      container.hidden = true;

      container.setAttribute(
        'aria-busy',
        'false'
      );

      return;
    }

    const list =
      container.querySelector(
        '[data-recommendations-list]'
      );

    if (list) {
      list.innerHTML = cards;
    }

    container.hidden = false;

    container.setAttribute(
      'aria-busy',
      'false'
    );

    requestAnimationFrame(() => {
      const currentViewport =
        container.querySelector(
          '[data-recommendations-viewport]'
        );

      if (currentViewport) {
        const maximumIndex =
          Math.max(
            0,
            products.length - 1
          );

        const restoredIndex =
          Math.min(
            previousIndex,
            maximumIndex
          );

        currentViewport.scrollLeft =
          currentViewport.clientWidth *
          restoredIndex;
      }

      this.bindRecommendationViewport();
      this.updateRecommendationCounter();
    });
  }

  buildRecommendationCard(
    product,
    currency,
    freeShippingRemaining
  ) {
    const availableVariants = (
      product.variants || []
    ).filter(
      (variant) => variant.available
    );

    if (!availableVariants.length) {
      return '';
    }

    const firstVariant =
      availableVariants[0];

    const imageUrl =
      this.getProductImage(
        product,
        firstVariant
      );

    const productUrl =
      product.url ||
      `/products/${product.handle}`;

    const unlocksFreeShipping =
      freeShippingRemaining > 0 &&
      Number(firstVariant.price) >=
        freeShippingRemaining;

    const variantControl =
      availableVariants.length > 1
        ? `
          <label
            class="visually-hidden"
            for="CartDrawer-RecommendationVariant-${product.id}"
          >
            Choose an option
          </label>

          <select
            id="CartDrawer-RecommendationVariant-${product.id}"
            class="gt-cart-recommendation__select"
            data-recommendation-variant
            data-currency="${this.escapeAttribute(
              currency
            )}"
          >
            ${availableVariants
              .map((variant) => {
                const variantImage =
                  this.getProductImage(
                    product,
                    variant
                  );

                return `
                  <option
                    value="${this.escapeAttribute(
                      String(variant.id)
                    )}"
                    data-price="${Number(
                      variant.price
                    )}"
                    data-image="${this.escapeAttribute(
                      variantImage
                    )}"
                  >
                    ${this.escapeHTML(
                      variant.public_title ||
                        variant.title
                    )}
                  </option>
                `;
              })
              .join('')}
          </select>
        `
        : '';

    const variantLabel =
      availableVariants.length === 1 &&
      firstVariant.public_title
        ? `
          <div class="gt-cart-recommendation__single-variant">
            ${this.escapeHTML(
              firstVariant.public_title
            )}
          </div>
        `
        : '';

    return `
      <article
        class="gt-cart-recommendation"
        data-recommendation-card
      >
        <a
          class="gt-cart-recommendation__image-link"
          href="${this.escapeAttribute(
            productUrl
          )}"
        >
          <img
            class="gt-cart-recommendation__image"
            data-recommendation-image
            src="${this.escapeAttribute(
              imageUrl
            )}"
            alt="${this.escapeAttribute(
              product.title
            )}"
            width="160"
            height="160"
            loading="lazy"
          >
        </a>

        <div class="gt-cart-recommendation__content">
          <a
            class="gt-cart-recommendation__title"
            href="${this.escapeAttribute(
              productUrl
            )}"
          >
            ${this.escapeHTML(
              product.title
            )}
          </a>

          ${
            variantControl ||
            variantLabel
          }

          <span
            class="gt-cart-recommendation__unlock"
            data-free-shipping-unlock
            ${
              unlocksFreeShipping
                ? ''
                : 'hidden'
            }
          >
            Unlocks free shipping
          </span>
        </div>

        <div
          class="gt-cart-recommendation__price"
          data-recommendation-price
        >
          ${this.escapeHTML(
            this.formatMoney(
              Number(
                firstVariant.price
              ),
              currency
            )
          )}
        </div>

        <button
          class="gt-cart-recommendation__add"
          type="button"
          data-recommendation-add
          data-product-title="${this.escapeAttribute(
            product.title
          )}"
          data-variant-id="${this.escapeAttribute(
            String(firstVariant.id)
          )}"
        >
          <span class="gt-cart-recommendation__add-label">
            <b aria-hidden="true">+</b>
            Add
          </span>

          <span
            class="gt-cart-recommendation__button-spinner"
            aria-hidden="true"
          ></span>
        </button>
      </article>
    `;
  }

  updateRecommendationShippingBadge(
    card,
    variantPrice
  ) {
    if (!card) return;

    const container = card.closest(
      '#CartDrawer-Recommendations'
    );

    const badge = card.querySelector(
      '[data-free-shipping-unlock]'
    );

    if (!container || !badge) {
      return;
    }

    const remaining =
      Number(
        container.dataset
          .freeShippingRemaining || 0
      );

    badge.hidden = !(
      remaining > 0 &&
      variantPrice >= remaining
    );
  }

  scrollRecommendations(direction) {
    const viewport =
      this.querySelector(
        '[data-recommendations-viewport]'
      );

    if (!viewport || !direction) {
      return;
    }

    viewport.scrollBy({
      left:
        viewport.clientWidth *
        direction,

      behavior: 'smooth',
    });
  }

  async addRecommendedProduct(button) {
    const variantId =
      Number(
        button.dataset.variantId
      );

    if (
      !variantId ||
      button.disabled
    ) {
      return;
    }

    const productTitle =
      button.dataset.productTitle ||
      'Product';

    const status =
      this.querySelector(
        '[data-recommendations-status]'
      );

    button.disabled = true;

    button.classList.add(
      'is-loading'
    );

    if (status) {
      status.textContent = '';
    }

    try {
      const root =
        window.Shopify?.routes?.root ||
        '/';

      const response = await fetch(
        `${root}cart/add.js`,
        {
          method: 'POST',

          headers: {
            'Content-Type':
              'application/json',

            Accept:
              'application/json',

            'X-Requested-With':
              'XMLHttpRequest',
          },

          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: 1,
              },
            ],

            sections:
              this.getSectionsToRender()
                .map(
                  (section) =>
                    section.id
                ),

            sections_url:
              window.location.pathname,
          }),
        }
      );

      const data =
        await response.json();

      if (
        !response.ok ||
        data.status
      ) {
        throw new Error(
          data.description ||
            data.message ||
            'This product could not be added.'
        );
      }

      if (
        typeof publish === 'function' &&
        window.PUB_SUB_EVENTS
          ?.cartUpdate
      ) {
        publish(
          PUB_SUB_EVENTS.cartUpdate,
          {
            source:
              'cart-recommendation',

            productVariantId:
              String(variantId),

            cartData: data,
          }
        );
      }

      this.recommendationsKey = '';

      this.renderContents(data, {
        toastMessage:
          `${productTitle} added to your cart`,

        preserveScroll: true,
      });
    } catch (error) {
      console.error(error);

      button.disabled = false;

      button.classList.remove(
        'is-loading'
      );

      if (status) {
        status.textContent =
          error.message ||
          'This product could not be added.';
      }
    }
  }

  getAppliedDiscountCodes() {
    const dataElement =
      this.querySelector(
        '[data-applied-discount-codes]'
      );

    if (!dataElement) return [];

    try {
      const codes = JSON.parse(
        dataElement.textContent ||
          '[]'
      );

      if (!Array.isArray(codes)) {
        return [];
      }

      return codes
        .map((code) =>
          String(code).trim()
        )
        .filter(Boolean);
    } catch (error) {
      console.error(
        'Unable to read applied discount codes:',
        error
      );

      return [];
    }
  }

  normaliseDiscountCodes(codes) {
    const uniqueCodes = new Map();

    codes.forEach((code) => {
      const cleanCode =
        String(code).trim();

      if (!cleanCode) return;

      uniqueCodes.set(
        cleanCode.toLowerCase(),
        cleanCode
      );
    });

    return Array.from(
      uniqueCodes.values()
    );
  }

  async updateDiscountCodes(codes) {
    const root =
      window.Shopify?.routes?.root ||
      '/';

    const response = await fetch(
      `${root}cart/update.js`,
      {
        method: 'POST',

        headers: {
          'Content-Type':
            'application/json',

          Accept:
            'application/json',

          'X-Requested-With':
            'XMLHttpRequest',
        },

        body: JSON.stringify({
          discount:
            this.normaliseDiscountCodes(
              codes
            ).join(','),

          sections:
            this.getSectionsToRender()
              .map(
                (section) =>
                  section.id
              ),

          sections_url:
            window.location.pathname,
        }),
      }
    );

    const data =
      await response.json();

    if (
      !response.ok ||
      data.errors ||
      data.status
    ) {
      throw new Error(
        data.description ||
          data.message ||
          data.errors ||
          'The discount could not be updated.'
      );
    }

    return data;
  }

  async removeDiscountCode(button) {
    if (
      button.disabled ||
      button.classList.contains(
        'is-loading'
      )
    ) {
      return;
    }

    const code =
      String(
        button.dataset.discountCode ||
          ''
      ).trim();

    if (!code) return;

    const existingCodes =
      this.getAppliedDiscountCodes();

    const remainingCodes =
      existingCodes.filter(
        (existingCode) =>
          existingCode.toLowerCase() !==
          code.toLowerCase()
      );

    button.disabled = true;

    button.classList.add(
      'is-loading'
    );

    try {
      const data =
        await this.updateDiscountCodes(
          remainingCodes
        );

      this.renderContents(data, {
        preserveScroll: true,

        toastMessage:
          `${code} discount removed`,
      });
    } catch (error) {
      console.error(error);

      button.disabled = false;

      button.classList.remove(
        'is-loading'
      );

      this.showToast(
        error.message ||
          'The discount could not be removed.'
      );
    }
  }

  async applyDiscount(form) {
    const input = form.querySelector(
      '[name="discount"]'
    );

    const button = form.querySelector(
      'button[type="submit"]'
    );

    const code =
      input?.value.trim();

    const currentMessage =
      form.querySelector(
        '[data-discount-message]'
      );

    if (!code) {
      if (currentMessage) {
        currentMessage.textContent =
          'Enter a gift voucher or discount code.';

        currentMessage.classList.add(
          'is-error'
        );
      }

      input?.focus();
      return;
    }

    button.disabled = true;

    button.classList.add(
      'is-loading'
    );

    if (currentMessage) {
      currentMessage.textContent = '';

      currentMessage.classList.remove(
        'is-success',
        'is-error'
      );
    }

    try {
      const existingCodes =
        this.getAppliedDiscountCodes();

      const nextCodes =
        this.normaliseDiscountCodes([
          ...existingCodes,
          code,
        ]);

      const data =
        await this.updateDiscountCodes(
          nextCodes
        );

      const appliedDiscount = (
        data.discount_codes || []
      ).find(
        (discount) =>
          discount.code?.toLowerCase() ===
          code.toLowerCase()
      );

      const wasApplied =
        appliedDiscount
          ? appliedDiscount.applicable !==
            false
          : this.cartContainsDiscountCode(
              data,
              code
            );

      this.renderContents(data, {
        preserveScroll: true,
      });

      window.setTimeout(() => {
        const details =
          this.querySelector(
            '.gt-cart-discount-details'
          );

        const newInput =
          this.querySelector(
            '#CartDrawer-DiscountCode'
          );

        const newMessage =
          this.querySelector(
            '[data-discount-message]'
          );

        if (details) {
          details.open = true;
        }

        if (
          newInput &&
          !wasApplied
        ) {
          newInput.value = code;
        }

        if (newMessage) {
          newMessage.textContent =
            wasApplied
              ? 'Code applied successfully.'
              : 'That code is not valid for these items.';

          newMessage.classList.toggle(
            'is-success',
            wasApplied
          );

          newMessage.classList.toggle(
            'is-error',
            !wasApplied
          );
        }
      }, 80);
    } catch (error) {
      console.error(error);

      button.disabled = false;

      button.classList.remove(
        'is-loading'
      );

      if (currentMessage) {
        currentMessage.textContent =
          error.message ||
          'The code could not be applied.';

        currentMessage.classList.add(
          'is-error'
        );
      }
    }
  }

  cartContainsDiscountCode(
    data,
    code
  ) {
    const codes = Array.isArray(
      data.discount_codes
    )
      ? data.discount_codes
      : [];

    return codes.some(
      (discount) =>
        discount.code?.toLowerCase() ===
          code.toLowerCase() &&
        discount.applicable !== false
    );
  }

  showToast(message) {
    const toast =
      this.querySelector(
        '[data-cart-toast]'
      );

    if (!toast || !message) return;

    clearTimeout(this.toastTimer);

    toast.textContent = message;

    toast.classList.add(
      'is-visible'
    );

    this.toastTimer =
      window.setTimeout(() => {
        toast.classList.remove(
          'is-visible'
        );

        window.setTimeout(() => {
          if (
            !toast.classList.contains(
              'is-visible'
            )
          ) {
            toast.textContent = '';
          }
        }, 220);
      }, 2400);
  }

  getProductImage(
    product,
    variant
  ) {
    const variantImage =
      variant?.featured_image;

    const productImage =
      product.featured_image;

    let source = '';

    if (
      typeof variantImage === 'string'
    ) {
      source = variantImage;
    } else if (variantImage?.src) {
      source = variantImage.src;
    } else if (
      typeof productImage === 'string'
    ) {
      source = productImage;
    } else if (productImage?.url) {
      source = productImage.url;
    } else if (productImage?.src) {
      source = productImage.src;
    } else if (
      Array.isArray(product.images) &&
      product.images.length
    ) {
      source =
        typeof product.images[0] ===
        'string'
          ? product.images[0]
          : product.images[0]?.src ||
            '';
    }

    if (!source) return '';

    const separator =
      source.includes('?')
        ? '&'
        : '?';

    return `${source}${separator}width=256`;
  }

  formatMoney(cents, currency) {
    const value =
      Number(cents || 0) / 100;

    try {
      return new Intl.NumberFormat(
        document.documentElement.lang ||
          'en-AU',
        {
          style: 'currency',

          currency:
            currency || 'AUD',

          currencyDisplay:
            'narrowSymbol',

          minimumFractionDigits: 2,
        }
      ).format(value);
    } catch (error) {
      return `$${value.toFixed(2)}`;
    }
  }

  escapeHTML(value = '') {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  escapeAttribute(value = '') {
    return this.escapeHTML(value);
  }
}

if (
  !customElements.get('cart-drawer')
) {
  customElements.define(
    'cart-drawer',
    CartDrawer
  );
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

if (
  !customElements.get(
    'cart-drawer-items'
  )
) {
  customElements.define(
    'cart-drawer-items',
    CartDrawerItems
  );
}